/**
 * Phone and email checks shared by the web app and the API, so a client sees the same answer in
 * the form that the server gives. Phone numbers must be valid mobile numbers for their prefix.
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile';
/**
 * A mobile number in its national form, checked against the dialling code (e.g. '+33', '6 12 34 56 78').
 * Returns the digits to store, or an error message. `country` names it in the message ("Italy (+39)").
 */
export function checkMobile(dialCode, national, country) {
    if (!/^\+\d{1,4}$/.test(dialCode))
        return { ok: false, error: 'Select a country code.' };
    const invalid = `Enter a valid mobile number for ${country ? `${country} (${dialCode})` : dialCode}.`;
    const digits = national.replace(/[\s().-]/g, '');
    if (!/^\d+$/.test(digits))
        return { ok: false, error: 'Phone numbers can only contain digits.' };
    if (digits.length < 4 || digits.length > 15)
        return { ok: false, error: invalid };
    // A leading trunk 0 (as written at home, e.g. 06 12 34 56 78 in France) is accepted and dropped.
    const p = parsePhoneNumberFromString(`${dialCode}${digits}`) ?? parsePhoneNumberFromString(`${dialCode}${digits.replace(/^0+/, '')}`);
    if (!p || !p.isValid())
        return { ok: false, error: invalid };
    return { ok: true, national: String(p.nationalNumber), e164: String(p.number) };
}
/**
 * An international number ('+447911123456', spaces allowed) split into the dialling code and the
 * national digits as the profile stores them, or null when it is not a valid mobile number.
 */
export function splitE164(raw) {
    const s = raw.replace(/[\s().-]/g, '');
    if (!/^\+\d{6,16}$/.test(s))
        return null;
    const p = parsePhoneNumberFromString(s);
    if (!p || !p.isValid())
        return null;
    return { phoneCc: `+${p.countryCallingCode}`, phone: String(p.nationalNumber) };
}
/** Same rule as the API: one @, a domain with a dot and a 2+ letter ending, no spaces or double dots. */
const EMAIL_FORMAT = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\.[A-Za-z]{2,24}$/;
/** Returns why an email address can't be right, or null. Doesn't check that it receives mail. */
export function emailProblem(raw) {
    const email = raw.trim();
    if (!email)
        return 'Enter your email address.';
    if (email.length > 254)
        return 'Email address is too long.';
    if (!EMAIL_FORMAT.test(email))
        return 'Enter a valid email address, such as name@company.com.';
    const [local] = email.split('@');
    if (local.length > 64 || local.startsWith('.') || local.endsWith('.') || email.includes('..'))
        return 'Enter a valid email address, such as name@company.com.';
    return null;
}
const COMMON_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'live.com', 'aol.com', 'protonmail.com', 'proton.me', 'gmx.com', 'mail.ru', 'yandex.ru', 'hotmail.co.uk', 'yahoo.co.uk', 'orange.fr', 'web.de'];
function distance(a, b) {
    const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
    for (let j = 1; j <= b.length; j++)
        d[0][j] = j;
    for (let i = 1; i <= a.length; i++)
        for (let j = 1; j <= b.length; j++)
            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    return d[a.length][b.length];
}
/** A likely typo in a well-known email domain ("gmial.com"): the corrected address, or null. */
export function emailSuggestion(raw) {
    const email = raw.trim().toLowerCase();
    const at = email.lastIndexOf('@');
    if (at < 1)
        return null;
    const domain = email.slice(at + 1);
    if (!domain || COMMON_DOMAINS.includes(domain))
        return null;
    let best = null;
    let bestD = 3;
    for (const d of COMMON_DOMAINS) {
        const dist = distance(domain, d);
        if (dist < bestD) {
            best = d;
            bestD = dist;
        }
    }
    // Short domains are often real (a.co, b.io): only near-certain typos of longer ones.
    return best && bestD <= (domain.length >= 8 ? 2 : 1) && domain.length >= 6 ? `${email.slice(0, at)}@${best}` : null;
}
//# sourceMappingURL=contact.js.map